import React from 'react';
import { ShieldCheckIcon, XMarkIcon } from './icons';

interface PlanSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (plan: 'standard' | 'pro' | 'pro_max') => void;
  currentPlan?: 'standard' | 'pro' | 'pro_max' | null;
}

const plans = [
  {
    id: 'standard' as const,
    name: 'Standard',
    price: '¥500',
    description: '10枚（買い切り）',
    features: ['高解像度', '全機能利用可'],
    popular: true,
    buttonText: '購入する'
  },
  {
    id: 'pro' as const,
    name: 'Pro',
    price: '¥1,000',
    description: '25枚（買い切り）',
    features: ['高解像度', '全機能利用可'],
    popular: false,
    buttonText: '購入する'
  },
  {
    id: 'pro_max' as const,
    name: 'Pro Max',
    price: '¥5,000/月',
    description: '月200枚',
    features: ['高解像度', '全機能利用可'],
    popular: false,
    buttonText: '購入する'
  }
];

export const PlanSelectionModal: React.FC<PlanSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectPlan,
  currentPlan
}) => {
  if (!isOpen) return null;

  // プラン選択の制御
  const isCurrentPlan = (planId: 'standard' | 'pro' | 'pro_max'): boolean => {
    // スタンダードプランは買い切りなので追加購入可能
    if (planId === 'standard') {
      return false;
    }
    return currentPlan === planId;
  };

  const isDowngrade = (planId: 'standard' | 'pro' | 'pro_max'): boolean => {
    console.log('🔍 isDowngrade 判定:', { currentPlan, planId });
    
    if (!currentPlan) {
      console.log('🔍 currentPlan が null/undefined');
      return false;
    }
    
    // スタンダードプランは買い切りなので追加購入可能（ダウングレードではない）
    if (planId === 'standard') {
      console.log('🔍 Standardプランは買い切りなので追加購入可能');
      return false;
    }
    
    // Pro Max → Pro への変更
    if (currentPlan === 'pro_max' && planId === 'pro') {
      console.log('🔍 Pro Max → Pro への変更を検出');
      return true;
    }
    
    console.log('🔍 ダウングレードではない');
    return false;
  };

  const handlePlanSelect = (planId: 'standard' | 'pro' | 'pro_max') => {
    console.log('🔍 handlePlanSelect 呼び出し:', { currentPlan, planId, isDowngrade: isDowngrade(planId) });
    
    if (isDowngrade(planId)) {
      const currentPlanName = currentPlan === 'pro_max' ? 'Pro Max' : 
                             currentPlan === 'pro' ? 'Pro' : 'Standard';
      const selectedPlanName = planId === 'pro_max' ? 'Pro Max' : 
                              planId === 'pro' ? 'Pro' : 'Standard';
      
      console.log('🔍 プランダウングレード検出:', { currentPlanName, selectedPlanName });
      
      const confirmed = window.confirm(
        `現在${currentPlanName}プランに加入しています。\n` +
        `${selectedPlanName}プランに変更すると、機能が制限されます。\n\n` +
        `本当に変更しますか？`
      );
      
      console.log('🔍 確認結果:', confirmed);
      
      if (!confirmed) {
        console.log('🔍 ユーザーがキャンセル、処理を中断');
        return;
      }
    }
    
    console.log('🔍 onSelectPlan 呼び出し:', planId);
    onSelectPlan(planId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              プランを選択してください
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <p className="text-slate-600 mb-8 text-center">
            高解像度で保存するには、以下のプランからお選びください
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-xl border-2 p-6 transition-all hover:shadow-lg ${
                  plan.popular
                    ? 'border-blue-500 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      おすすめ
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="text-3xl font-bold text-slate-900 mb-2">
                    {plan.price}
                  </div>
                  <p className="text-slate-600 text-sm">
                    {plan.description}
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <ShieldCheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePlanSelect(plan.id)}
                  disabled={(plan.id !== 'standard' && isCurrentPlan(plan.id)) || (currentPlan === 'pro_max' && plan.id === 'pro')}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    (plan.id !== 'standard' && isCurrentPlan(plan.id))
                      ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                      : (currentPlan === 'pro_max' && plan.id === 'pro')
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : isDowngrade(plan.id)
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : plan.id === 'standard'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {(plan.id !== 'standard' && isCurrentPlan(plan.id)) ? '現在のプラン' : 
                   (currentPlan === 'pro_max' && plan.id === 'pro') ? 'ダウングレード不可' :
                   isDowngrade(plan.id) ? 'プランを変更する' : plan.buttonText}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm">
              決済はStripeを通じて安全に行われます
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
